<template>
  <div id="latest-feed" ref="feed">
    <section class="container-fluid mb-5">
      <div class="d-flex justify-content-center">
        <div class="latest-header text-center">
          <section-header title="Fresh Artwork" subtitle="New Stuff ✨" />
          <p v-if="!isLoggedIn">
            Customize this feed by
            <a href="#" @click.prevent="showLoginModal">creating an account</a>
            and following your favorite artists.
          </p>
          <button-radio-group
            :options="types"
            button-classes="btn-secondary btn-sm latest-toggle"
            v-model="selectedType"
            @change="handleToggle(selectedType)"
          >
          </button-radio-group>
        </div>
      </div>
      <div class="row">
        <div class="masonry" v-if="feedArtwork.length > 0">
          <div
            v-for="photo in feedArtwork.slice(feedStart, feedEnd)"
            v-bind:key="photo.artist_id + '-' + photo.id"
            class="item"
          >
            <div class="latest-card">
              <base-button
                v-if="photo.type !== 'animation'"
                class="preview-icon"
                @click="showPreview(photo.url_path)"
                aria-label="Preview artwork"
              >
                <i class="fas fa-eye"></i>
              </base-button>
              <div class="latest-card-header d-flex align-items-center">
                <router-link
                  class="artist-avatar"
                  :to="'/art/' + photo.handle.replace('@', '')"
                >
                  <img
                    :src="photo.profile_image"
                    :alt="photo.handle + 'profile image'"
                    class="latest-profile rounded-circle"
                    height="40"
                    width="40"
                  />
                </router-link>
                <router-link
                  class="artist-link"
                  :to="'/art/' + photo.handle.replace('@', '')"
                >
                  <h3 class="ellipsis">
                    {{ photo.handle
                    }}<verified-badge
                      v-if="photo.is_verified"
                      size="smmd"
                      :showTooltip="false"
                    />
                  </h3>
                </router-link>
                <base-button
                  class="action-button add-to-collection"
                  @click="
                    handleAddToCollection(
                      photo.id,
                      photo.poster_url_path || photo.url_path,
                      photo.type
                    )
                  "
                  aria-label="Add artwork to collection"
                >
                  <i
                    v-if="!isInCollection(photo.id, photo.type)"
                    class="far fa-heart"
                  ></i>
                  <i v-else class="fa fa-heart"></i>
                </base-button>
              </div>
              <router-link
                class="artist-link"
                :to="getArtworkLink(photo.id, photo.handle, photo.type)"
              >
                <div class="latest-card-image-wrapper">
                  <div v-if="photo.pro_file_id" class="pro-file-icon">
                    <badge type="success">PRO</badge>
                  </div>
                  <img
                    v-if="photo.type === 'image'"
                    :src="photo.url_path"
                    :alt="photo.handle + ' artwork #' + photo.id"
                    class="latest-card-image"
                    width="480"
                    height="480"
                  />
                  <div v-if="photo.type === 'animation'" class="video-wrapper">
                    <i class="fas fa-play-circle" />
                    <video
                      width="100%"
                      loop
                      :ref="'anim-' + photo.id"
                      :poster="photo.poster_url_path"
                      :src="photo.url_path"
                      @mouseover="playVideo('anim-' + photo.id)"
                      @mouseleave="pauseVideo('anim-' + photo.id)"
                      preload="none"
                      muted
                    />
                  </div>
                </div>
              </router-link>
              <div
                class="latest-footer d-flex justify-content-between align-items-center mt-2"
              >
                <div class="price">
                  <strong v-if="photo.price">${{ photo.price }}</strong>
                </div>
                <div class="photo-created">
                  <small>{{ timeSince(photo.created) }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="!loading" class="not-following">
          <div class="text-center d-flex flex-column not-following px-5">
            <i class="far fa-frown-open"></i>It looks like you're not following
            any artists yet. Try following some of our featured artists.
            <div class="mt-3">
              <base-button size="sm" type="outline-primary" @click="goToArtists"
                >FEATURED ARTISTS</base-button
              >
            </div>
          </div>
        </div>
      </div>
      <div v-if="feedArtwork.length > 0 && !loading" class="my-4 text-center">
        <!-- <base-button
          size="sm"
          type="outline-primary"
          @click="loadMore"
          ref="loadbutton"
          >LOAD MORE</base-button
        > -->
        <base-pagination
          class="mt-2"
          :page-count="pageCount"
          v-model="page"
          align="center"
          @pageChange="feedPageChange"
        ></base-pagination>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  // CallToActionSection,
  ButtonRadioGroup,
  SectionHeader,
  VerifiedBadge,
} from "../../components";

const apiBaseURL = process.env.VUE_APP_API_URL;
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "latest-page",
  metaInfo() {
    return {
      title: "Latest Artwork and Animations - Art Grab",
      meta: [
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: `summary_large_image`,
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `Latest Artwork and Animations - Art Grab`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `Check out the most recent artwork uploaded to Art Grab.`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content: "https://i.imgur.com/gp9AfzO.jpeg",
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `https://artgrab.co/latest`,
        },
        {
          vmid: "og:title",
          property: "og:title",
          content: `Latest Artwork and Animations - Art Grab`,
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: `Check out the most recent artwork uploaded to Art Grab.`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content: "https://i.imgur.com/gp9AfzO.jpeg",
        },
        {
          vmid: "description",
          property: "description",
          content: `Check out the most recent artwork uploaded to Art Grab.`,
        },
      ],
      link: [{ rel: "canonical", href: `${artgrabURL}/latest` }],
    };
  },
  data() {
    return {
      photos: [],
      page: 1,
      loading: false,
      followLoading: false,
      types: [
        { value: "featured", label: "FEATURED" },
        { value: "following", label: "FOLLOWING" },
      ],
      selectedType: "featured",
      // feedStart: 0,
    };
  },
  components: {
    // CallToActionSection,
    ButtonRadioGroup,
    SectionHeader,
    VerifiedBadge,
  },
  computed: {
    ...mapState([
      "user",
      "isLoggedIn",
      "feedType",
      "feedLoaded",
      "feedArtwork",
      "feedEnd",
      "feedPage",
    ]),
    followOrUnfollow() {
      return this.isFollowing();
    },
    feedStart() {
      return (this.page - 1) * 100;
    },
    pageCount() {
      return Math.ceil(this.feedArtwork.length / 100);
    },
  },
  methods: {
    feedPageChange() {
      this.loading = true;
      this.$store.commit("setFeedPage", this.page);
      this.$store.commit("setFeedEnd", this.feedStart + 100);
      this.scrollToElement("latest-feed");
      this.loading = false;
    },
    showPreview(thumbnail) {
      this.$store.commit("setPreviewImage", thumbnail);
      this.$store.commit("showPreviewsModal", true);
    },
    playVideo(id) {
      this.$refs[id][0].play();
    },
    pauseVideo(id) {
      this.$refs[id][0].pause();
    },
    getArtworkLink(id, handle, type) {
      return `/${type === "animation" ? "animation" : "art"}/${handle.replace(
        "@",
        ""
      )}/${id}`;
    },
    scrollToElement: function (element_id) {
      // var bodyRect = document.body.getBoundingClientRect();
      // var elemRect = document
      //   .getElementById(element_id)
      //   .getBoundingClientRect();
      // var offset = elemRect.top - bodyRect.top;

      window.scrollTo({
        left: 0,
        top: 0,
        behavior: "auto",
      });
    },
    showLoginModal() {
      this.$store.commit("showLoginModal", true);
    },
    loadMore() {
      this.$store.commit("setFeedEnd", this.feedEnd + 100);
    },
    goToArtists() {
      this.$router.push({ path: "/artists" });
    },
    handleToggle(type) {
      this.$store.commit("setFeedType", type);
    },
    timeSince(date) {
      const gmtDate = date.replace(/\s/, "T") + "Z";
      const createdDate = new Date(gmtDate);
      return `${
        createdDate.getMonth() + 1
      }/${createdDate.getDate()}/${createdDate.getFullYear()} ${createdDate.toLocaleTimeString()}`;
    },
    isFollowing() {
      if (this.isLoggedIn) {
        return (
          this.user.following.filter(
            (artist) => artist.artist_id == this.artist_id
          ).length > 0
        );
      }
      return false;
    },
    isInCollection(id, type) {
      if (this.user.collections.length > 0) {
        return this.findDeep(this.user.collections, id, type);
      }
      return false;
    },
    handleAddToCollection(photo_id, sized_url, type = "image") {
      this.$store.commit("showAddToCollectionModal", {
        status: true,
        photo_id: photo_id,
        sized_url: sized_url,
        type: type,
      });
    },
    findDeep(collections, photo_id, type) {
      const self = this;
      if (type === "animation") {
        return collections.some(function (e) {
          if (e.animation_id == photo_id) {
            return true;
          } else if (e.animations) {
            return self.findDeep(e.animations, photo_id, type);
          }
        });
      } else {
        return collections.some(function (e) {
          if (e.photo_id == photo_id) {
            return true;
          } else if (e.photos) {
            return self.findDeep(e.photos, photo_id, type);
          }
        });
      }
    },
    handleFollow() {
      if (this.isLoggedIn) {
        this.followLoading = true;
        this.axios
          .post(`${apiBaseURL}/api/artv1.1/user/followartist`, {
            artist_id: this.artist_id,
            unfollow: this.isFollowing(),
          })
          .then((result) => {
            if (result.data.message === "Success") {
              if (result.data.data.message === "Success") {
                this.$store.commit("setFollowing", result.data.data.following);
              }
            }
          })
          .catch((error) => {
            /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
            console.error(error);
          })
          .finally(() => {
            this.followLoading = false;
          });
      } else {
        this.$store.commit("showLoginModal", true);
      }
    },
    getLatest() {
      this.loading = true;
      this.$store.commit("setFeedEnd", 100);
      let photos = [];
      let animations = [];

      this.axios
        .post(`${apiBaseURL}/api/artv1.1/photo/feed`, {
          type: this.feedType,
        })
        .then((result) => {
          if (result.data.data.photos.length > 0) {
            photos = result.data.data.photos;
            for (const photo of photos) {
              photo.url_path = `${process.env.VUE_APP_CDN_BASE_URL}${photo.url_path}`;
              photo.profile_image = `${process.env.VUE_APP_CDN_BASE_URL}${photo.profile_image}`;
              photo.is_featured = photo.is_featured === "1" ? true : false;
              photo.is_verified = photo.is_verified === "1" ? true : false;
              photo.type = "image";
            }
          }
          if (result.data.data.animations.length > 0) {
            animations = result.data.data.animations;
            for (const animation of animations) {
              animation.url_path = `${process.env.VUE_APP_ANIMATION_CDN_URL}${animation.url_path}`;
              animation.poster_url_path = `${process.env.VUE_APP_ANIMATION_CDN_URL}${animation.poster_url_path}`;
              animation.profile_image = `${process.env.VUE_APP_CDN_BASE_URL}${animation.profile_image}`;
              animation.is_verified =
                animation.is_verified === "1" ? true : false;
              animation.is_featured =
                animation.is_featured === "1" ? true : false;
              animation.type = "animation";
            }
          }

          const combined = [...photos, ...animations].sort(
            (a, b) => new Date(b.created) - new Date(a.created)
          );

          if (combined.length > 0) {
            this.$store.commit("setFeedArtwork", combined.slice(0, 1000));
            this.$store.commit("setFeedLoaded", true);
          } else {
            this.$store.commit("setFeedArtwork", []);
          }
        })
        .catch((error) => {
          /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted: function () {
    this.$store.commit("setLayout", "page-layout");
    if (!this.feedLoaded || this.feedArtwork.length === 0) {
      this.getLatest();
    }
    this.selectedType = this.feedType;
    this.page = this.feedPage;
    this.$store.commit("setFeedEnd", this.feedStart + 100);
  },
  watch: {
    feedType(newType, oldType) {
      this.getLatest();
      this.selectedType = newType;
      this.page = this.feedPage;
      this.$store.commit("setFeedEnd", this.feedStart + 100);
    },
  },
};
</script>

<style lang="scss">
#latest-feed {
  padding-top: 100px;

  .fa-play-circle {
    font-size: 50px;
    z-index: 2;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .latest-header {
    margin-top: 50px;
    margin-bottom: 30px;
    width: 100%;
    // max-width: 480px;
  }

  .artist-link-loader {
    width: 200px;
  }

  .loading-heart-icon {
    padding: 3px;
  }

  .masonry {
    margin: 1.5em auto;
    padding: 0 12px;
    column-gap: 1.5em;
  }

  .item {
    background: #fff;
    padding: 1em;
    margin: 0 0 1.5em;
    position: relative;
    max-width: 480px;
    break-inside: avoid;
    border: 1px solid #eee;
    border-radius: 6px;
  }

  .not-following {
    width: 100%;
    margin: 0 auto 30px;
    padding: 3.5rem 0;
    color: #666;
    background-color: #f7f7f7;

    i {
      font-size: 4rem;
      margin-bottom: 2rem;
    }
  }

  .latest-toggle {
    width: 100px;
  }

  .latest-card {
    // margin: 0 auto 60px auto;
    // max-width: 480px;
    position: relative;

    .preview-icon {
      position: absolute;
      bottom: 46px;
      left: 14px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      border: none;
      padding: 5px 3px;
      border-radius: 6px;
      z-index: 1;

      &:focus,
      &:active {
        background: black !important;
        color: white !important;
      }
    }

    .latest-profile {
      height: 40px;
      width: 40px;
      margin-right: 10px;
    }

    .latest-card-header {
      margin-bottom: 12px;
      display: flex;
      justify-content: space-between;

      .latest-handle {
        color: #000;
      }

      .artist-link {
        flex: 1;
        min-width: 0;

        h3 {
          font-weight: 400;
        }
      }

      .ellipsis {
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        display: block;
        margin: 0;
      }
    }

    .latest-card-image-wrapper {
      position: relative;

      .pro-file-icon {
        position: absolute;
        top: 10px;
        right: 12px;
        pointer-events: none;
      }

      .latest-card-image {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
      }

      .video-wrapper {
        &:hover {
          i {
            display: none;
          }
        }
      }

      video {
        border-radius: 4px;
      }
    }

    .action-button {
      padding: 0;
      background: none;
      border: 0;
      box-shadow: none;
      white-space: nowrap;
      margin-left: 20px;

      &.add-to-collection {
        i {
          font-size: 22px;
          color: #000;
          &:hover {
            color: #f5365c;
          }
        }
      }

      &:active {
        background: none;

        &.add-to-collection {
          i {
            color: lighten(#f5365c, 8%);
          }
        }
      }
    }
  }

  /* Masonry on large screens */
  @media only screen and (min-width: 1296px) {
    .masonry {
      column-count: 4;
    }
  }

  /* Masonry on medium-sized screens */
  @media only screen and (max-width: 1295px) and (min-width: 976px) {
    .masonry {
      column-count: 3;
    }
  }

  /* Masonry on small screens */
  @media only screen and (max-width: 975px) and (min-width: 626px) {
    .masonry {
      column-count: 2;
    }
  }
}

@media (max-width: 575.98px) {
  #latest-feed {
    .latest-header {
      margin-top: 0;
    }
  }
}
</style>
